import type { BranchState } from '../lib/branchState'
import { alertsToShow } from '../lib/queueAlerts'
import { formatMoment } from '../lib/staffText'
import { BellIcon } from './hallParts'
import { useStaffText } from './StaffText'
import '../styles/hall.css'

/**
 * A queue over its alert level, said at the top of a staff screen until it is
 * back under its reset level. Not dismissable: one person closing it would hide
 * the queue from the next person to look (Part A of the batch 4 part G plan).
 * role="status" rather than alert, because it is news, not an error in what the
 * person just did.
 *
 * A bell and one line per queue. `quiet` draws it as the Floor's note under its
 * sentence rather than as a notice box: on the Floor the lines themselves are on
 * screen, so the alert is a pointer to one of them, not the only word about it.
 */
export function QueueAlertBanner({
  state,
  queueIds,
  zone,
  quiet = false,
}: {
  state: BranchState | null
  queueIds?: readonly string[]
  /** The branch's time zone: "since 10:30" is a moment of the branch's day. */
  zone?: string
  quiet?: boolean
}) {
  const { lang, say } = useStaffText()
  if (!state) return null
  const shown = alertsToShow(state, lang, queueIds)
  if (shown.length === 0) return null
  return (
    <ul className={quiet ? 'hall-alerts hall-alerts--quiet' : 'hall-alerts'}>
      {shown.map((a) => (
        <li key={a.queueId} className="hall-alert" role="status">
          <BellIcon />
          <span>
            {say(a.reason === 'waiting' ? 'notify.banner.waiting' : 'notify.banner.longest', {
              queue: a.queue,
              n: a.waiting,
              time: formatMoment(a.since, lang, { time: true, zone }),
            })}
          </span>
        </li>
      ))}
    </ul>
  )
}
